import type { AxiosRequestConfig } from 'axios';
import type {
  ApiResponse,
  IAcceptVTORequest,
  IAssignAvailableShiftsRequest,
  IDeleteShiftRequest,
  IGetClientShiftDetails,
  IGetClientShifts,
  IGetSchedulesRequest,
  IHeadcountShiftRaw,
  IOfferShiftRequest,
  IScheduleDTO,
  IUpcomingShiftRaw,
  IUpdateShiftRequest,
  IUpsertShiftByDay,
  IUpsertShiftByWeek,
} from './types';
import type { IHeadcountShiftDetail } from '@/components/client/scheduling/types';
import type { StandardRequest } from '@/store';

import { v3Get, v3Post } from './axios.instance.V3';

const oldPrefix = '/agent/schedule';

const prefix = {
  sp: {
    schedule: '/sp/schedule',
  },
  su: {
    schedule: '/su/schedule',
  },
};

const url = {
  sp: {
    getSchedules: (startBlock: number, endBlock: number) => `${oldPrefix}/list/${startBlock}/${endBlock}`,
  },
  su: {
    getClientSchedules: (clientId: number) => `${prefix.su.schedule}/list?clientId=${clientId}`,
    getClientShifts: ({ weekId, scheduleId }: IGetClientShifts) =>
      `${prefix.su.schedule}/${scheduleId}/weekshifts/${weekId}`,
    getClientShiftDetails: ({ scheduleId, shiftId }: IGetClientShiftDetails) =>
      `${prefix.su.schedule}/${scheduleId}/shift/${shiftId}`,
    upsertShiftByWeek: () => `${prefix.su.schedule}/shift/upsertbydayofweek`,
    upsertShiftByDay: () => `${prefix.su.schedule}/shift/upsert`,
    updateShift: ({ shiftId, scheduleId, usersDesired }: IUpdateShiftRequest) =>
      `${prefix.su.schedule}/${scheduleId}/shift/${shiftId}/update?usersDesired=${usersDesired}`,
    deleteShift: ({ shiftId, scheduleId }: IDeleteShiftRequest) =>
      `${prefix.su.schedule}/shift/${shiftId}/delete?scheduleId=${scheduleId}`,
  },
  getShifts: (startBlock: number, endBlock: number) =>
    `${prefix.sp.schedule}/shifts/list/assigned/blockrange/${startBlock}/${endBlock}`,
  getAvailableShifts: (startBlock: number, endBlock: number) =>
    `${oldPrefix}/shifts/available/${startBlock}/${endBlock}`,
  assignAvailableShifts: `${oldPrefix}/shifts/assign`,
  offerShift: `${oldPrefix}/shifts/offer`,
  acceptVTO: `${oldPrefix}/shifts/vto`,
  getUpcomingShifts: `${oldPrefix}/shifts/upcoming/list`,
} as const;

const getSchedules = (payload: IGetSchedulesRequest): ApiResponse =>
  v3Get(url.sp.getSchedules(payload.startBlock, payload.endBlock));

const getShifts = (payload: IGetSchedulesRequest, config?: AxiosRequestConfig): ApiResponse =>
  v3Get(url.getShifts(payload.startBlock, payload.endBlock), config);

const getAvailableShifts = (payload: IGetSchedulesRequest, config?: AxiosRequestConfig): ApiResponse =>
  v3Get(url.getAvailableShifts(payload.startBlock, payload.endBlock), config);

const getUpcomingShifts: StandardRequest<IUpcomingShiftRaw[]> = config => v3Get(url.getUpcomingShifts, config);

const assignAvailableShifts = (payload: IAssignAvailableShiftsRequest, config?: AxiosRequestConfig): ApiResponse =>
  v3Post(url.assignAvailableShifts, payload, config);

const offerShift = (payload: IOfferShiftRequest, config?: AxiosRequestConfig): ApiResponse =>
  v3Post(url.offerShift, payload, config);

const acceptVTO = (payload: IAcceptVTORequest): ApiResponse => v3Post(url.acceptVTO, payload);

/**
 * SU
 */

const getClientSchedules: StandardRequest<IScheduleDTO[], { clientId: number }> = (payload, config) =>
  v3Get(url.su.getClientSchedules(payload.clientId), config);

const getClientShifts: StandardRequest<IHeadcountShiftRaw[][], IGetClientShifts> = (payload, config) =>
  v3Get(url.su.getClientShifts(payload), config);

const getClientShiftDetails: StandardRequest<IHeadcountShiftDetail, IGetClientShiftDetails> = (payload, config) =>
  v3Get(url.su.getClientShiftDetails(payload), config);

const upsertShiftByWeek: StandardRequest<boolean, IUpsertShiftByWeek> = (payload, config) =>
  v3Post(url.su.upsertShiftByWeek(), payload, config);

const upsertShiftByDay: StandardRequest<boolean, IUpsertShiftByDay> = (payload, config) =>
  v3Post(url.su.upsertShiftByDay(), payload, config);

const updateShift: StandardRequest<boolean, IUpdateShiftRequest> = (payload, config) =>
  v3Post(url.su.updateShift(payload), config);

const deleteShift: StandardRequest<boolean, IDeleteShiftRequest> = (payload, config) =>
  v3Get(url.su.deleteShift(payload), config);

export const apiSchedule = {
  //SP
  getSchedules,
  getUpcomingShifts,
  getShifts,
  getAvailableShifts,
  assignAvailableShifts,
  offerShift,
  acceptVTO,
  //SU
  getClientSchedules,
  getClientShifts,
  getClientShiftDetails,
  upsertShiftByWeek,
  upsertShiftByDay,
  updateShift,
  deleteShift,
};
